import { BufferAttribute, Color, Group, Mesh, Object3D, PlaneGeometry } from 'three'
import { unityPosition, unityQuaternion } from '../core/unity'
import type { AssetLibrary, ManifestNode } from './assetLibrary'
import { applyShadowCasting } from './shadowCasting'

export interface PrefabOptions {
  /**
   * Rename materials as the tree is built — the port of `Bus.SwitchColor`,
   * which swaps the renderer's material rather than tinting it.
   */
  material?: (name: string) => string
  /** Multiply the base colour of these materials. A fallback for statics. */
  tint?: Color
  /** Only tint materials whose name is in this set. Omit to tint everything. */
  tintOnly?: ReadonlySet<string>
  /** Skip nodes whose name matches. */
  skip?: (node: ManifestNode) => boolean
  /** Keep nodes the prefab had switched off. Off by default. */
  includeInactive?: boolean
}

/**
 * Instantiate a manifest prefab node tree as an `Object3D`.
 *
 * Local transforms are converted from Unity space per node rather than by
 * scaling a parent: a negative scale would flip winding again on every child
 * and fight the conversion already baked into the geometry.
 *
 * Call after `library.loadMeshes(...)` — meshes are looked up synchronously so
 * the tree is built in one pass.
 *
 * 📐 **There was a `forceVisible` option here, and deleting it is a finding
 * rather than a tidy-up (PS-8).** It existed to draw the bus's `Ice/Fake` node,
 * which carries the whole `Bus_N_single` mesh with its MeshRenderer switched
 * off. Nothing ever passed it — ten call sites, none — and it could not have
 * fired anyway: `Fake` sits under `Ice`, and `Ice` is not in `DRAWN_SUBTREES`,
 * so `buildPrefab` is never handed a subtree that contains it. Dead twice over.
 *
 * What that deadness is evidence **for** outlives the code: the port draws a
 * bus as the stretchable **Head + Middle + Tail** assembly, never as the single
 * mesh. If the single mesh were the drawn body, something would have had to
 * reach for this option. `busGeometry.test.ts` holds the other half — `Fake`'s
 * renderer really is off, its scale really is 0.90277, and `DRAWN_SUBTREES`
 * really excludes `Ice`.
 *
 * ⚠️ The old comment on the option also said `Fake` is simply "a mesh nobody
 * draws". True but incomplete — it is the ShapeModule emitter mesh for the
 * `ice_vfx` particle system (`ConveyorBox (M).prefab`:17948). That correction
 * belongs to IC-6 and is not made here.
 */
export function buildPrefab(
  library: AssetLibrary,
  node: ManifestNode,
  options: PrefabOptions = {},
): Object3D {
  const object = new Group()
  object.name = node.name

  unityPosition(node.position.x, node.position.y, node.position.z, object.position)
  unityQuaternion(node.rotation.x, node.rotation.y, node.rotation.z, node.rotation.w, object.quaternion)
  object.scale.set(node.scale.x, node.scale.y, node.scale.z)

  // 🔍 A node whose MeshRenderer the prefab switched off is **not drawn**, full
  // stop. There used to be a `forceVisible` option here to override that; it
  // was dead twice over and is gone (PS-8). See the note above `buildPrefab`.
  if (node.mesh && node.visible) {
    const geometry = library.peekMesh(node.mesh)
    if (geometry) {
      const resolve = (name: string | undefined) => {
        const swapped = name && options.material ? options.material(name) : name
        const tint =
          options.tint && (!options.tintOnly || (swapped && options.tintOnly.has(swapped)))
            ? options.tint
            : undefined
        return library.material(swapped ?? '', tint)
      }

      // One material per draw group, so a mesh with submeshes keeps them — the
      // bus body is its colour plus a `BusShadows` layer, and collapsing to a
      // single material loses the second.
      //
      // A submesh past the end of the renderer's material list takes the
      // **last** material, not the first. 📐 Unity's rule for a renderer with
      // fewer materials than the mesh has submeshes (an engine rule, nothing
      // in the kit spells it out). Exactly one node in the bundle is in that
      // position: `Elevator/Lift`, whose `Lift.obj` has three groups against
      // `[Lift2, Invisible]` — so submesh 2, the rim around the shaft mouth,
      // is drawn with `Invisible` and paints nothing, same as submesh 1.
      // Falling back to `materials[0]` gave it `Lift2` and put a plate the
      // original never shows across the mouth (ELV-6).
      const groups = geometry.groups.length
      const last = node.materials[node.materials.length - 1]
      const material =
        groups > 1
          ? Array.from({ length: groups }, (_, i) => resolve(node.materials[i] ?? last))
          : resolve(node.materials[0])

      // The vertex-colour mask stays raw on the geometry.
      //
      // The shader resolves it against the material's palette, which is what
      // the original does — `Bus.SwitchColor` swaps the material and never
      // touches the mesh. Resolving on the CPU meant a geometry clone per
      // colour, and it threw away the mask that `_MatcapChannelMask` and the
      // per-channel detail maps still need downstream.
      const mesh = new Mesh(geometry, material)
      mesh.name = `${node.name}#mesh`
      // Which manifest mesh this came from — the node name is not it, and
      // callers need it to find side data such as blend shapes.
      mesh.userData.meshName = node.mesh
      // 📐 `MeshRenderer.m_CastShadows` and the materials' SHADOWCASTER pass —
      // see `applyShadowCasting`. A manifest from before LGT-1 has no flag, and
      // On is Unity's default.
      applyShadowCasting(mesh, node.castShadows !== false)
      object.add(mesh)
    }
  }

  if (node.sprite && node.visible) {
    // A sprite lies in the node's local XY plane, facing -Z, exactly as Unity
    // draws it — the transform already tilts it toward the camera.
    const sprite = node.sprite
    const geometry = new PlaneGeometry(sprite.size[0], sprite.size[1])
    const [u0, v0, u1, v1] = sprite.uv
    geometry.setAttribute(
      'uv',
      new BufferAttribute(new Float32Array([u0, v1, u1, v1, u0, v0, u1, v0]), 2),
    )
    const quad = new Mesh(geometry, library.spriteMaterial(sprite))
    quad.name = `${node.name}#sprite`
    // Sprites sit on top of the sign they label, not inside it.
    quad.renderOrder = 1
    object.add(quad)
  }

  for (const child of node.children) {
    if (!options.includeInactive && !child.active) continue
    if (options.skip?.(child)) continue
    object.add(buildPrefab(library, child, options))
  }

  return object
}

/** Find a node by name anywhere in a manifest tree. */
export function findNode(node: ManifestNode, name: string): ManifestNode | null {
  if (node.name === name) return node
  for (const child of node.children) {
    const hit = findNode(child, name)
    if (hit) return hit
  }
  return null
}
